import { useState } from "react";
import { partyRoles, linkStatus } from "@/mocks/partes";

interface NewPartyLinkModalProps {
  onClose: () => void;
  onSubmit: (data: Record<string, string>) => void;
  preselectedParty?: string;
}

export default function NewPartyLinkModal({ onClose, onSubmit, preselectedParty }: NewPartyLinkModalProps) {
  const [form, setForm] = useState<Record<string, string>>({
    party: preselectedParty || "", role: "", project: "", environmentalUnit: "",
    sourceDocument: "", responsibility: "", startDate: "", endDate: "", status: "Ativo", notes: "",
  });

  const update = (key: string, value: string) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <h3 className="text-lg font-semibold text-foreground-900 font-heading">Novo Vínculo</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-50 transition-colors">
            <i className="ri-close-line w-full h-full flex items-center justify-center text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Parte *</label>
            <input required type="text" value={form.party} onChange={(e) => update("party", e.target.value)} placeholder="Nome da parte envolvida" className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Papel exercido *</label>
            <select required value={form.role} onChange={(e) => update("role", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
              <option value="">Selecione...</option>
              {partyRoles.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Projeto vinculado</label>
            <select value={form.project} onChange={(e) => update("project", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
              <option value="">Selecione...</option>
              <option value="PRJ-2026-005">Implantação do CTA — Goiânia Centro</option>
              <option value="PRJ-2026-008">Parque Municipal Ambiental Norte</option>
              <option value="PRJ-2026-007">Loteamento Verde Vale</option>
              <option value="PRJ-2026-012">Projeto Carbono Urbano — Áreas Verdes Municipais</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Unidade ambiental vinculada</label>
            <input type="text" value={form.environmentalUnit} onChange={(e) => update("environmentalUnit", e.target.value)} placeholder="Área verde, UC, árvore CTA..." className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Documento fonte</label>
            <input type="text" value={form.sourceDocument} onChange={(e) => update("sourceDocument", e.target.value)} placeholder="ART, contrato, autorização..." className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 placeholder:text-foreground-400" />
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Responsabilidade</label>
            <textarea rows={2} value={form.responsibility} onChange={(e) => update("responsibility", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900 resize-none" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Data início</label>
              <input type="date" value={form.startDate} onChange={(e) => update("startDate", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-600 mb-1">Data fim</label>
              <input type="date" value={form.endDate} onChange={(e) => update("endDate", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900" />
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-foreground-600 mb-1">Status</label>
            <select value={form.status} onChange={(e) => update("status", e.target.value)} className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 bg-white text-foreground-900">
              {linkStatus.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </form>
        <div className="border-t border-background-200 px-6 py-4 flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground-600 bg-background-50 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">Cancelar</button>
          <button type="submit" onClick={handleSubmit} className="px-4 py-2 text-sm font-semibold text-white bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap">
            <i className="ri-save-line w-4 h-4 inline-flex items-center justify-center mr-1" />
            Criar vínculo
          </button>
        </div>
      </div>
    </div>
  );
}